import { PlayStylePlus } from "@/components/molecules/Player/PlayStylePlus/PlayStylePlus";
import { GAME_ASSET_SLUG } from "@/config/game";
import { ChemistryChip } from "@/design-system/Chemistry/Chemistry";
import { extractColors } from "@/functions/extractCardColors";
import { EvolutionsIcon } from "@/icons";
import { cn } from "@/lib/utils";
import {
  contentLabelsSelector,
  eventTokensSelector,
  getVersionAssetsSelector,
} from "@/redux/loadedContent/loadedContentSlice";
import { useTranslation } from "@repo/shared/i18n";
import {
  BasePlayer,
  Chemistry,
  isEvolution as isEvolutionFunction,
} from "@repo/shared/types";
import { usesFullPlayerImage } from "@repo/shared/utils";
import { useSelector } from "react-redux";

import { CardEffects } from "./CardEffects";
import { FIRST_OWNER_GLYPH } from "./PlayerCardView";
import { Price } from "./Price";

const BASE_WIDTH = 190;
const BASE_HEIGHT = Math.round((BASE_WIDTH * 900) / 644);

const FONT_MEDIUM = "CruyffMedium";
const FONT_CONDENSED = "CruyffCondensedMedium";
const FONT_NUMBERS_MEDIUM = "CruyffNumbersMedium";
const FONT_JALLIYA = "JalliyaSeven";

export const playerCardCondensedSize = (scale: number) => ({
  width: BASE_WIDTH * scale,
  height: BASE_HEIGHT * scale,
});

const fallbackArtUrl = (resourceId: number) =>
  `${import.meta.env.VITE_APP_ASSETS_BASE_URL}/${GAME_ASSET_SLUG}/playercards/small/${resourceId}.png`;

/*
  What the card draws. The identity fields are required; everything the
  composite needs is optional, because the SBC solver's players are the same
  cards under different field names and can only supply the first three.
*/
export type CondensedCardPlayer = Pick<
  BasePlayer,
  "resourceId" | "cardName" | "priceInfo"
> &
  Partial<
    Pick<
      BasePlayer,
      | "rating"
      | "preferredPosition"
      | "countryId"
      | "leagueId"
      | "clubId"
      | "versionId"
      | "metalId"
      | "playerUrl"
      | "hasDynamicImage"
      | "cardFont"
      | "foil"
      | "cosmetics"
    >
  > & {
    /** Only when the player has no card name; the card name is what it shows. */
    displayName?: string;
  };

const PlayerCardCondensed = ({
  player,
  scale = 1,
  position,
  isEvolution,
  hidePrice = false,
  priceSize = "medium",
  untradeable = false,
  firstOwner,
  playStylesPlus,
  chemistry,
  previousChemistry,
  chemistryScale = 1,
}: {
  player: CondensedCardPlayer;
  scale?: number;
  position?: string;
  isEvolution?: boolean;
  hidePrice?: boolean;
  /** The pill is text and never scales with the art; pick its step instead. */
  priceSize?: "small" | "medium";
  /** A club card that cannot be sold: marked on the price pill. */
  untradeable?: boolean;
  /** EA's first-owner glyph under the position. */
  firstOwner?: boolean;
  /** PlayStyle+ badges down the card's left edge. */
  playStylesPlus?: number[];
  /**
   * The player's chemistry (0–3), as a ChemistryChip on the card's bottom-left
   * corner, just overlapping it. Hidden when omitted.
   */
  chemistry?: Chemistry;
  /** The chemistry before a change, so the chip shows what was gained or lost. */
  previousChemistry?: Chemistry;
  /**
   * The chip's own scale, apart from the card's, so it can match a meta rating
   * chip shown with the card. Full size by default.
   */
  chemistryScale?: number;
}) => {
  const { t } = useTranslation("common");
  const versionAssets = useSelector(getVersionAssetsSelector);
  const { positions } = useSelector(contentLabelsSelector);
  const eventTokens = useSelector(eventTokensSelector);

  const {
    resourceId,
    cardName,
    cardFont,
    foil,
    cosmetics,
    displayName,
    rating,
    preferredPosition,
    countryId,
    leagueId,
    clubId,
    versionId,
    metalId,
    playerUrl,
    hasDynamicImage,
    priceInfo,
  } = player;

  const composited = versionId != null && metalId != null && !!playerUrl;
  const { topText, bottomText, cardUrl, isLight, strip } = extractColors({
    versionAssets,
    versionId: versionId ?? 0,
    metalId: metalId ?? 1,
  });

  const slot = position ?? preferredPosition;
  const evolution = isEvolution ?? isEvolutionFunction(player as BasePlayer);
  const useFullImage = usesFullPlayerImage(hasDynamicImage, versionId);

  const flagSrc = (kind: "countries" | "leagues" | "clubs", id: number) => {
    const isDark = kind !== "countries" && !isLight;
    return `${import.meta.env.VITE_APP_ASSETS_BASE_URL}/${GAME_ASSET_SLUG}/${kind}/${isDark ? "dark/" : ""}${id}.png`;
  };

  return (
    <div
      style={playerCardCondensedSize(scale)}
      className="relative block shrink-0"
    >
      {chemistry != null && (
        <ChemistryChip
          as="span"
          chemistry={chemistry}
          previousChemistry={previousChemistry}
          className="absolute bottom-1/20 left-0 z-10 origin-bottom-left -translate-x-1/6"
          style={{ scale: chemistryScale }}
        />
      )}
      <div
        className="relative"
        style={{
          width: BASE_WIDTH,
          height: BASE_HEIGHT,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          ...(composited && {
            backgroundImage: `url(${cardUrl})`,
            backgroundSize: "100% 100%",
            backgroundRepeat: "no-repeat",
          }),
        }}
      >
        {composited ? (
          <>
            {/* Over the version art, under every sibling below — which must be positioned. */}
            <CardEffects foil={foil} cosmetics={cosmetics} cardUrl={cardUrl} />
            <img
              src={playerUrl + "?v5"}
              alt={cardName}
              loading="lazy"
              decoding="async"
              className={
                useFullImage
                  ? "absolute -top-1 left-0 w-full pointer-events-none"
                  : "absolute top-[17%] left-[57%] w-[65%] -translate-x-1/2 pointer-events-none"
              }
            />
            <div
              className="absolute top-[21%] left-[13%] flex flex-col leading-none"
              style={{ color: topText }}
            >
              <span
                className="text-3xl"
                style={{ fontFamily: FONT_NUMBERS_MEDIUM }}
              >
                {rating}
              </span>
              {slot && (
                <span
                  className="-mt-2 mx-auto text-sm tracking-wide"
                  style={{ fontFamily: FONT_CONDENSED }}
                >
                  {positions[slot]?.name ?? slot}
                </span>
              )}
              {firstOwner && (
                <span
                  className="mx-auto text-xs -mt-1"
                  style={{ fontFamily: "UltimateTeamIcons" }}
                >
                  {FIRST_OWNER_GLYPH}
                </span>
              )}
            </div>
            <div
              className="absolute inset-x-[8%] bottom-[16%] flex flex-col items-center gap-y-1.5"
              style={{ color: bottomText }}
            >
              <div
                className={cn(
                  "max-w-full truncate text-xl leading-none",
                  // Room for the script's tall strokes inside the clip box.
                  cardFont === "jalliya" && "p-[0.5em] -m-[0.5em]",
                )}
                style={{
                  fontFamily:
                    cardFont === "jalliya" ? FONT_JALLIYA : FONT_MEDIUM,
                }}
              >
                {cardName || displayName}
              </div>
              <div className="flex items-center justify-center gap-x-2">
                {countryId != null && (
                  <img
                    alt={t("alt.country")}
                    src={flagSrc("countries", countryId)}
                    className={cn(cardFont === "jalliya" ? "h-3" : "h-3.5")}
                  />
                )}
                {leagueId != null && (
                  <img
                    alt={t("alt.league")}
                    src={flagSrc("leagues", leagueId)}
                    className={cn(cardFont === "jalliya" ? "h-4" : "h-5")}
                  />
                )}
                {clubId != null && (
                  <img
                    alt={t("alt.club")}
                    src={flagSrc("clubs", clubId)}
                    className={cn(cardFont === "jalliya" ? "h-4" : "h-5")}
                  />
                )}
              </div>
            </div>
            {playStylesPlus && playStylesPlus.length > 0 && (
              <div className="absolute top-[36%] left-[3%] flex flex-col">
                {playStylesPlus.map((id, index) => (
                  <div
                    key={id}
                    className={cn(
                      "size-7",
                      playStylesPlus.length > 3 && index > 0 && "-mt-1.5",
                    )}
                  >
                    <PlayStylePlus
                      id={id}
                      primaryColor={bottomText}
                      secondaryColor={strip}
                    />
                  </div>
                ))}
              </div>
            )}
            {evolution && (
              <div className="absolute bottom-[8%] right-[9%] size-7">
                <EvolutionsIcon
                  outlineColor={topText}
                  backgroundColor={strip}
                />
              </div>
            )}
          </>
        ) : (
          <img
            src={fallbackArtUrl(resourceId)}
            alt={cardName}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-contain"
          />
        )}
      </div>
      {/* Outside the transform: the price is text and keeps its size at every
          card scale. Only its perch on the card follows the scale. */}
      {!hidePrice && (
        <div
          className="absolute inset-x-0 flex justify-center -mt-1"
          style={{ top: 16 * scale }}
        >
          <Price
            size={priceSize}
            priceInfo={priceInfo}
            eventTokens={eventTokens}
            untradeable={untradeable}
          />
        </div>
      )}
    </div>
  );
};

export const PlayerCardCondensedSkeleton = ({
  scale = 1,
}: {
  scale?: number;
}) => (
  <div
    style={playerCardCondensedSize(scale)}
    className="shrink-0 rounded-md bg-neutral-800 animate-pulse"
  />
);

export default PlayerCardCondensed;
